import { AlignmentType } from 'docx'
import {
  createEmptyLine,
  createParagraph,
  formatPresentationScore,
} from './docxFunctions'
import { createRegionsDocxBlocks } from '@/components/reportPresentation/presentationRegionsDocx'
import {
  BOLD_TEXT,
  DEFAULT_TEXT,
  createCombinationParagraph,
} from './docxFunctions'

function scorePluralWord(n) {
  return n === 1 ? 'балл' : n < 5 ? 'балла' : 'баллов'
}

function parseScoreNumber(value) {
  const n = Number(String(value ?? '').replace(',', '.').trim())
  return Number.isFinite(n) ? n : NaN
}

function formatGvsScoreValue(value) {
  const formatted = formatPresentationScore(value)
  if (!formatted) return formatted
  return Number.isFinite(parseScoreNumber(value)) ? `+${formatted}` : formatted
}

function countByQLevel(items) {
  const counts = { high: 0, medium: 0, low: 0, other: 0 }
  for (const item of items || []) {
    const q = String(item?.qLevels || '').toLowerCase()
    if (q.includes('высок')) counts.high++
    else if (q.includes('средн')) counts.medium++
    else if (q.includes('низк')) counts.low++
    else if (q.trim()) counts.other++
  }
  return counts
}

function formatKeiAvbIntro(additionalInfo) {
  const kei = additionalInfo?.kei || []
  const avb = additionalInfo?.avb || []
  const kc = countByQLevel(kei)
  const ac = countByQLevel(avb)

  const parts = []
  if (kei.length) {
    const detail = []
    if (kc.high) detail.push(`${kc.high} КЭИ высокого качества`)
    if (kc.medium) detail.push(`${kc.medium} КЭИ среднего качества`)
    if (kc.low) detail.push(`${kc.low} КЭИ низкого качества`)
    if (!detail.length) detail.push(`${kei.length} КЭИ`)
    parts.push(detail.join(', '))
  } else {
    parts.push('0 КЭИ')
  }

  if (avb.length) {
    const detail = []
    if (ac.high) detail.push(`${ac.high} АВБ высокого качества`)
    if (ac.medium) detail.push(`${ac.medium} АВБ среднего качества`)
    if (ac.low) detail.push(`${ac.low} АВБ низкого качества`)
    if (!detail.length) detail.push(`${avb.length} АВБ`)
    parts.push(detail.join(' и '))
  } else {
    parts.push('0 АВБ')
  }

  return parts.join(' и ')
}

const COMMENT_TEXT_RUN = {
  italics: true,
  font: 'Times New Roman',
}

function commentBullets(comments) {
  const blocks = []
  const list = (Array.isArray(comments) ? comments : [comments])
    .map(c => String(c || '').trim())
    .filter(Boolean)

  if (!list.length) return blocks

  blocks.push(
    createParagraph(
      'Комментарии:',
      { alignment: AlignmentType.JUSTIFIED },
      COMMENT_TEXT_RUN
    )
  )
  blocks.push(createEmptyLine())
  list.forEach(line => {
    blocks.push(
      createParagraph(
        `- ${line}`,
        { alignment: AlignmentType.JUSTIFIED },
        COMMENT_TEXT_RUN
      )
    )
  })
  return blocks
}

function formatEpScoreSuffix(score) {
  const n = parseScoreNumber(score)
  if (!Number.isFinite(n) || n <= 0) return ''
  return `(+ ${formatPresentationScore(score)} ${scorePluralWord(n)})`
}

/** Балл по оценке КИ (как «Балл по оценке КИ» в блоке ГВС). */
function getGvsKiScore(org) {
  const fromQuality = Number(org?.qualityAssessment?.scoreEO)
  if (Number.isFinite(fromQuality) && fromQuality > 0) return fromQuality
  const fallback = Number(org?.scoreEO || org?.sumScore)
  return Number.isFinite(fallback) && fallback > 0 ? fallback : 0
}

function formatKiAssessmentClause(score) {
  const n = parseScoreNumber(score)
  if (!Number.isFinite(n) || n <= 0) return ''
  return `(при этом по результатам оценки качества клинических исследований было присвоено + ${formatPresentationScore(score)} ${scorePluralWord(n)})`
}

function createCenterEpConclusionBlocks(assessmentEp, listLabel) {
  const blocks = []
  const economic = Array.isArray(assessmentEp?.economic)
    ? assessmentEp.economic
    : []
  const items = economic.filter(
    item =>
      String(item?.condition || '').trim() ||
      Number(item?.resultScore) > 0
  )

  const buildText = (condition, resultScore) => {
    const indication = String(condition || '').trim() || '—'
    const scoreSuffix = formatEpScoreSuffix(resultScore)
    const scorePart = scoreSuffix ? ` ${scoreSuffix}` : ''
    return (
      'По результатам проведенной оценки экономических последствий применения лекарственного препарата ' +
      'сделан вывод о возможности / невозможности рекомендовать к включению лекарственный препарат ' +
      `в перечень ${listLabel}${scorePart} с учетом представленных экономических исследований по показанию: [${indication}]`
    )
  }

  if (!items.length) {
    blocks.push(
      createParagraph(buildText('', 0), { alignment: AlignmentType.JUSTIFIED })
    )
    return blocks
  }

  items.forEach((item, idx) => {
    if (idx > 0) blocks.push(createEmptyLine())
    blocks.push(
      createParagraph(buildText(item.condition, item.resultScore), {
        alignment: AlignmentType.JUSTIFIED,
      })
    )
  })

  return blocks
}

function pickGvsSummarySource(gvsList) {
  return (
    gvsList.find(item => item.lpPoints || item.ownScores) || gvsList[0] || null
  )
}

function formatGvsPointsLine(item) {
  if (!item?.lpPoints) return ''
  let line = `По результатам рассмотрения ЛП ${
    item.lpPoints === 'Да' ? 'набрал' : 'не набрал'
  } рекомендованное количество баллов`
  if (item.notDidPoints) {
    line += ` по шкалам (${item.notDidPoints})`
  }
  return `${line}.`
}

function formatGvsScoreLines(ownScores) {
  if (!ownScores) return []

  const lines = []
  if (ownScores.ki != null) {
    lines.push(
      `Оценка качества клинических исследований: ${formatGvsScoreValue(ownScores.ki)} баллов;`
    )
  }

  const econ = ownScores.economic
  if (econ != null && econ !== '') {
    lines.push(
      econ === 'не представляется возможным оценить' ||
        Number.isNaN(parseScoreNumber(econ))
        ? 'Оценка экономических последствий: не представляется возможным оценить;'
        : `Оценка экономических последствий: ${formatGvsScoreValue(econ)} баллов;`
    )
  } else {
    lines.push('Оценка экономических последствий: не представляется возможным оценить;')
  }

  if (ownScores.other != null) {
    lines.push(
      `Оценка дополнительных данных: ${formatGvsScoreValue(ownScores.other)} баллов.`
    )
  } else if (lines.length) {
    lines[lines.length - 1] = lines[lines.length - 1].replace(/;$/, '.')
  }

  return lines
}

function gvsBlocks(gvsList, listLabel) {
  const blocks = []
  if (!gvsList?.length) return blocks

  blocks.push(
    createCombinationParagraph([
      { type: BOLD_TEXT, content: '3. Главные внештатные специалисты (ГВС):' },
    ])
  )

  gvsList.forEach((item, index) => {
    const num = gvsList.length > 1 ? `${index + 1}.` : ''
    const expedient = item.conclusionExpediency
      ? 'целесообразным'
      : 'нецелесообразным'

    blocks.push(
      createCombinationParagraph([
        {
          type: DEFAULT_TEXT,
          content: `${num} Главный внештатный специалист ${item.gvsSpec || ''} Минздрава России (${item.gvsFio || ''}) считает ${expedient} включение препарата в перечень ${listLabel}.`,
        },
      ])
    )

    const otherScore = item.ownScores?.other
    if (otherScore != null && otherScore !== '') {
      const n = parseScoreNumber(otherScore)
      if (Number.isFinite(n)) {
        blocks.push(
          createParagraph(
            `(+ ${formatPresentationScore(otherScore)} ${scorePluralWord(n)} по оценке дополнительных данных)`,
            { alignment: AlignmentType.JUSTIFIED }
          )
        )
      }
    }

    blocks.push(...commentBullets(item.comments))

    if (index < gvsList.length - 1) {
      blocks.push(createEmptyLine())
    }
  })

  const summaryItem = pickGvsSummarySource(gvsList)
  const pointsLine = summaryItem ? formatGvsPointsLine(summaryItem) : ''
  const scoreLines = summaryItem ? formatGvsScoreLines(summaryItem.ownScores) : []

  if (pointsLine || scoreLines.length) {
    blocks.push(createEmptyLine())
    if (pointsLine) {
      blocks.push(
        createParagraph(pointsLine, { alignment: AlignmentType.JUSTIFIED })
      )
    }
    scoreLines.forEach(line => {
      blocks.push(createParagraph(line, { alignment: AlignmentType.JUSTIFIED }))
    })
  }

  blocks.push(createEmptyLine())
  return blocks
}

export function createAssessmentPresentationBlocks(
  summary,
  { otherExpertise, plpRegionText, listLabel, assessmentEp } = {}
) {
  const listArr = []
  if (summary?.gnvlp) listArr.push('ЖНВЛП')
  if (summary?.vzn14) listArr.push('ВЗН14')
  const list = listArr.join(', ')

  const org = summary?.assessmentOrganization || {}
  const blocks = []

  const intro = formatKeiAvbIntro(summary?.additionalInfo)
  blocks.push(
    createCombinationParagraph([
      {
        type: BOLD_TEXT,
        size: 32,
        content: `По результатам оценки методологического качества экономических исследований (${intro}) предложение направлено на количественную комплексную оценку в:`,
      },
    ])
  )
  blocks.push(createEmptyLine())

  if (org.nameExpOrg) {
    blocks.push(
      createCombinationParagraph([
        { type: BOLD_TEXT, content: `1. ${org.nameExpOrg}` },
      ])
    )
    blocks.push(createEmptyLine())

    const expedientWord = org.conclusionExpediency
      ? 'целесообразности включения'
      : 'нецелесообразности включения'
    const kiClause = formatKiAssessmentClause(getGvsKiScore(org))

    blocks.push(
      createCombinationParagraph([
        { type: DEFAULT_TEXT, content: 'По результатам проведенной оценки сделан вывод о ' },
        { type: BOLD_TEXT, content: expedientWord },
        {
          type: DEFAULT_TEXT,
          content: ` препарата в перечень ${list}${kiClause ? ` ${kiClause}` : ''}.`,
        },
      ])
    )

    blocks.push(...commentBullets(org.comments))
    blocks.push(createEmptyLine())
  }

  blocks.push(
    createCombinationParagraph([
      { type: BOLD_TEXT, content: '2. Центр экспертизы:' },
    ])
  )
  blocks.push(...createCenterEpConclusionBlocks(assessmentEp, list))
  blocks.push(createEmptyLine())

  blocks.push(...gvsBlocks(summary?.gvs || [], list))

  blocks.push(
    ...createRegionsDocxBlocks({
      plpRegionText,
      otherExpertise,
      listLabel: list,
    })
  )

  return blocks
}
